import { Search } from 'lucide-react';
import ArticleCard from '../components/community/ArticleCard';
import ArticleModal from '../components/community/ArticleModal.tsx';
import { Article } from '../../../shared/types/index.ts';
import { useEffect, useState } from 'react';
import { Pagination } from '../components/shared/Pagination.tsx';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/UI/LoadingSpinner.tsx';
import { Helmet } from 'react-helmet-async';
import { PLACEHOLDER_LECTURES } from '../data/placeholderLectures';
import { LECTURE_PAGE_TYPES } from '../../../shared/types/index.ts';

export default function Lectures() {
  const ARTICLES_PER_PAGE = 6;
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [visibleArticles, setVisibleArticles] = useState<Article[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') || 1);
  const search = searchParams.get('search');

  const [totalPages, setTotalPages] = useState<number>(1);
  const [loadingArticles, setLoadingArticles] = useState(false);

  const updatePage = (newPage: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('page', String(newPage));
      return params;
    });
  };

  const updateSearch = (nextSearch: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('search', String(nextSearch));
      params.set('page', '1');
      return params;
    });
  };

  useEffect(() => {
    const fetchArticles = async () => {
      setLoadingArticles(true);

      try {
        const params = new URLSearchParams({
          limit: String(ARTICLES_PER_PAGE),
          page: String(page),
          categories: LECTURE_PAGE_TYPES.join(','),
          order: 'asc',
        });

        if (search) params.append('search', search);

        const res = await fetch(`/api/articles?${params.toString()}`);

        if (!res.ok) {
          const resError = await res.json().catch(() => ({}));
          throw new Error(resError.message || `Request failed (${res.status})`);
        }

        const data = await res.json();
        const articles: Article[] = data.articles ?? [];

        // Until MySQL is seeded with lecture rows, show local placeholders.
        if (articles.length === 0) {
          const filtered = sortByDateAsc(filterPlaceholders(search));
          setVisibleArticles(paginate(filtered, page, ARTICLES_PER_PAGE));
          setTotalPages(Math.max(1, Math.ceil(filtered.length / ARTICLES_PER_PAGE)));
          return;
        }

        const sorted = sortByDateAsc(articles);
        setTotalPages(data.totalPages || 1);
        setVisibleArticles(sorted);
      } catch (error) {
        console.error('Error fetching lectures:', error);
        const filtered = sortByDateAsc(filterPlaceholders(search));
        setVisibleArticles(paginate(filtered, page, ARTICLES_PER_PAGE));
        setTotalPages(Math.max(1, Math.ceil(filtered.length / ARTICLES_PER_PAGE)));
      } finally {
        setLoadingArticles(false);
      }
    };

    fetchArticles();
  }, [page, search]);

  useEffect(() => {
    if (search) setSearchTerm(search);
  }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateSearch(searchTerm);
    }
  };

  return (
    <div className="bg-gradient-to-b from-[#E6F7F4] to-[#C5E8E3] min-h-screen">
      <Helmet>
        <title>Lectures | BIOCAP Website</title>
        <meta
          name="description"
          content="BIOCAP lectures and educational talks on coastal infrastructure and marine ecosystems."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 items-center py-20">
            <h1 className="text-4xl font-bold">Lectures</h1>
            <p className="text-lg text-gray-600">
              Explore BIOCAP talks and educational presentations
            </p>
          </div>

          <div>
            <div className="flex">
              <div className="relative w-full">
                <Search className="absolute left-3 translate-y-[60%] size-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search lectures..."
                  onKeyDown={handleKeyDown}
                  className="flex h-9 border placeholder:text-sm border-input bg-background px-3 py-2 text-base pl-10 w-full md:w-80 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>

            {loadingArticles && (
              <div className="my-[120px]">
                <LoadingSpinner size={150} color="white" />
              </div>
            )}

            {!loadingArticles && visibleArticles.length === 0 && (
              <div className="flex flex-col justify-center items-center py-20">
                <p className="text-3xl">No lectures match your search.</p>
                <p className="text-gray-500 text-lg pt-2">
                  Try adjusting your search, or check back soon for new talks.
                </p>
              </div>
            )}
          </div>

          {!loadingArticles && visibleArticles.length !== 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 py-8">
              {visibleArticles.map((article) => (
                <div
                  key={article.id}
                  className="w-full cursor-pointer"
                  onClick={() => setSelectedArticle(article)}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
          )}

          <div>
            <Pagination page={page} totalPages={totalPages} onPageChange={updatePage} />
          </div>
        </div>

        {selectedArticle && (
          <ArticleModal article={selectedArticle} setSelectedArticle={setSelectedArticle} />
        )}
      </div>
    </div>
  );
}

function filterPlaceholders(search: string | null): Article[] {
  if (!search?.trim()) return PLACEHOLDER_LECTURES;
  const q = search.trim().toLowerCase();
  return PLACEHOLDER_LECTURES.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.text.toLowerCase().includes(q),
  );
}

/** Earliest lecture date first. */
function sortByDateAsc(items: Article[]): Article[] {
  return [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

function paginate(items: Article[], page: number, perPage: number): Article[] {
  const start = (page - 1) * perPage;
  return items.slice(start, start + perPage);
}
